import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';

import BenchlingConfig from 'BenchlingConfig';
import ChatUploadedFileChip from 'llm/chat-library/messages/ChatUploadedFileChip';
import {ProgressData} from 'llm/chat-library/progress/ProgressTypes';
import CitationSuperscript from 'llm/markdown/CitationSuperscript';
import InlineChart from 'llm/markdown/InlineChart';
import {
  LLMMarkdownDependenciesContext,
  useLLMMarkdownDependencies,
  type LLMMarkdownClassNames,
  type LLMMarkdownDependencies,
} from 'llm/markdown/LLMMarkdownDependenciesContext';
import {rehypeBenchlingTags} from 'llm/markdown/LLMMarkdownRehype';
import {remarkBenchlingTags, remarkCodeBlock} from 'llm/markdown/LLMMarkdownRemark';
import {
  LLMMarkdownTableMetadataProvider,
  useLLMMarkdownTableMetadata,
} from 'llm/markdown/LLMMarkdownTableMetadataContext';
import {
  BenchlingTagConfig,
  BenchlingTagConfigs,
  BenchlingTagProperty,
  extractTextFromReactNode,
  isAllowedUrl,
  isItalicOnlyMarkdownParagraph,
  parseBnchFileJSON,
} from 'llm/markdown/LLMMarkdownUtils';
import TableWithCitations, {type LLMMarkdownTableClassNames} from 'llm/markdown/TableWithCitations';
import {InternalLink, isModelInternalLinkable} from 'notebook/mediocre/mentions/InternalLink';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {maybeGetSmartChipTypeFromId} from 'ui-library/chips/SmartChipTypeUtils';
import {GRAY_DARK, GRAY_LIGHT} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {remarkGfmTables} from 'ui-library/remarkGfmTables';
import {getAPIIDPrefix} from 'util/APIUtil';
import * as logging from 'util/Logging';

export type {LLMMarkdownClassNames};

export enum SaveToEntryOption {
  REMOVE = 'remove',
  PRESERVE_TEXT = 'preserve_text',
  PRESERVE_TAGS = 'preserve_tags',
}

function toTableClassNames(classNames?: LLMMarkdownClassNames): LLMMarkdownTableClassNames | undefined {
  if (!classNames) {
    return undefined;
  }
  return {
    simpleTableHeader: classNames.simpleTableHeader,
    simpleTableHeaderCell: classNames.simpleTableHeaderCell,
    simpleTableRow: classNames.simpleTableRow,
    tableCopyButton: classNames.tableCopyButton,
  };
}

function BenchlingIdChip({idValue}: Record<string, unknown>): JSX.Element {
  const {classNames: markdownClassNames} = useLLMMarkdownDependencies();
  const id = idValue as string;
  if (id && maybeGetSmartChipTypeFromId(id) != null) {
    return <BenchChip id={id} size={ChipSize.MINI} className={classNames(markdownClassNames?.chip)} />;
  }
  const modelType = id ? getAPIIDPrefix(id) : undefined;
  logging.info(`[llm] Invalid Benchling ID`, {extra: {id, modelType}});
  return <span>{id}</span>;
}

function BenchlingIdSavingComponent({idValue}: Record<string, unknown>): JSX.Element {
  const id = idValue as string;
  if (id && isModelInternalLinkable(id)) {
    return <InternalLink internalLink={{id, name: id}} />;
  }
  return <span>{id}</span>;
}

function CitationChipComponent({idValue}: Record<string, unknown>): JSX.Element {
  const {citationNumberMap, onCitationClick, progressData} = useLLMMarkdownDependencies();
  const citationId = idValue as string;
  const citationNumber = citationNumberMap?.get(citationId);
  return (
    <CitationSuperscript
      citationId={citationId}
      citationNumber={citationNumber}
      onCitationClick={onCitationClick}
      progressData={progressData}
    />
  );
}

/** Used by the custom `<p>` benchling tag config (block elements like charts), not GFM paragraphs. */
function ParagraphComponent({children}: Record<string, unknown>): JSX.Element {
  return <div className={css(styles.paragraph)}>{children}</div>;
}

function MarkdownParagraphWithTableMetadata({children}: Record<string, unknown>): JSX.Element {
  const {setPendingSubtitle} = useLLMMarkdownTableMetadata();
  if (isItalicOnlyMarkdownParagraph(children)) {
    const subtitle = extractTextFromReactNode(children).trim();
    if (subtitle.length > 0) {
      setPendingSubtitle(subtitle);
    }
  }
  return <div className={css(styles.paragraph)}>{children}</div>;
}

function MarkdownH2WithTableMetadata({children}: Record<string, unknown>): JSX.Element {
  const {setPendingTitle} = useLLMMarkdownTableMetadata();
  const title = extractTextFromReactNode(children).trim();
  if (title.length > 0) {
    setPendingTitle(title);
  }
  return <h2>{children}</h2>;
}

function MarkdownH3WithTableMetadata({children}: Record<string, unknown>): JSX.Element {
  const {setPendingTitle} = useLLMMarkdownTableMetadata();
  const title = extractTextFromReactNode(children).trim();
  if (title.length > 0) {
    setPendingTitle(title);
  }
  return <h3>{children}</h3>;
}

function MarkdownH4WithTableMetadata({children}: Record<string, unknown>): JSX.Element {
  const {setPendingTitle} = useLLMMarkdownTableMetadata();
  const title = extractTextFromReactNode(children).trim();
  if (title.length > 0) {
    setPendingTitle(title);
  }
  return <h4>{children}</h4>;
}

function TableComponent({children}: Record<string, unknown>): JSX.Element {
  const {classNames: markdownClassNames} = useLLMMarkdownDependencies();
  const {consumePendingMetadata} = useLLMMarkdownTableMetadata();
  const {title, subtitle} = consumePendingMetadata();
  return (
    <TableWithCitations classNames={toTableClassNames(markdownClassNames)} title={title} subtitle={subtitle}>
      {children}
    </TableWithCitations>
  );
}

function FileComponent({fileId, filename, mediaType}: Record<string, unknown>): JSX.Element {
  const {chatSessionId} = useLLMMarkdownDependencies();
  return (
    <ChatUploadedFileChip
      chatSessionId={chatSessionId ?? null}
      externalFileId={fileId as string}
      filename={filename as string}
      mediaType={mediaType as string}
    />
  );
}

function getCleanWebCitationUrl(originalUrl: string): string | null {
  try {
    const url = new URL(originalUrl);
    // Strip all parameters from the URL as a security mitigation.
    return `${url.origin}${url.pathname}`;
  } catch (error) {
    if (error instanceof TypeError) {
      return null;
    }
    throw error;
  }
}

function WebCitationComponent({linkUrl}: Record<string, unknown>): JSX.Element {
  const cleanUrl = getCleanWebCitationUrl(linkUrl as string);
  if (cleanUrl == null) {
    return <></>;
  }

  return (
    <span className={css(styles.webCitationContainer)}>
      <a href={cleanUrl} target="_blank" rel="noopener noreferrer" className={css(styles.webCitationLink)}>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.MINI}
          icon="link"
          {...getTestProps({
            component: 'LLMMarkdownSection',
            element: 'webCitationButton',
            key: cleanUrl,
          })}
        />
      </a>
    </span>
  );
}

function WebCitationSavingComponent({linkUrl}: Record<string, unknown>): JSX.Element {
  const cleanUrl = getCleanWebCitationUrl(linkUrl as string);
  if (cleanUrl == null) {
    return <></>;
  }

  return <a href={cleanUrl}>{cleanUrl}</a>;
}

/**
 * Factory function to create tag configs. Runtime dependencies (citations, classNames, etc.) are read from
 * `LLMMarkdownDependenciesContext` inside tag components — do not add inline component wrappers here.
 */
export const createBenchlingTagConfigs = (_deps: LLMMarkdownDependencies = {}): BenchlingTagConfigs => {
  const tagConfigs: Array<BenchlingTagConfig> = [
    {
      markdownTag: 'bnch_id',
      htmlTagName: 'idchip',
      properties: [BenchlingTagProperty.ID_VALUE],
      validator: (content: string) => /^[a-z]+_[a-zA-Z0-9]{8,16}$/.test(content),
      component: BenchlingIdChip,
      preserveContentWhenSaving: SaveToEntryOption.PRESERVE_TAGS,
      savingComponent: BenchlingIdSavingComponent,
    },
    {
      markdownTag: 'bnch_citation',
      htmlTagName: 'citationchip',
      properties: [BenchlingTagProperty.ID_VALUE],
      validator: (content: string) => content.length > 0,
      component: CitationChipComponent,
      preserveContentWhenSaving: SaveToEntryOption.REMOVE,
    },
    // This is a hack to allow block elements like charts to be rendered.
    // This pattern should not be used.
    {
      markdownTag: 'p',
      htmlTagName: 'p',
      properties: [],
      validator: () => true,
      component: ParagraphComponent,
      preserveContentWhenSaving: SaveToEntryOption.PRESERVE_TEXT,
    },
    {
      markdownTag: 'bnch_chart',
      htmlTagName: 'benchlingchart',
      properties: [BenchlingTagProperty.ID_VALUE],
      validator: (content: string) => /^chart_[a-zA-Z0-9]{8,16}$/.test(content),
      // InlineChart reads its dependencies from LLMMarkdownDependenciesContext rather than
      // closing over `deps`, so the component reference stays stable across re-renders and the
      // chart does not remount (see the "does not remount chart components" test).
      component: InlineChart,
      preserveContentWhenSaving: SaveToEntryOption.PRESERVE_TEXT,
    },
    {
      markdownTag: 'bnch_file',
      htmlTagName: 'benchlingfile',
      properties: [
        BenchlingTagProperty.FILE_ID,
        BenchlingTagProperty.FILENAME,
        BenchlingTagProperty.MEDIA_TYPE,
      ],
      validator: (content: string) => parseBnchFileJSON(content) != null,
      component: FileComponent,
      preserveContentWhenSaving: SaveToEntryOption.PRESERVE_TEXT,
    },
    {
      markdownTag: null,
      htmlTagName: 'table',
      properties: [],
      validator: (content: string) => content.length > 0,
      component: TableComponent,
      preserveContentWhenSaving: SaveToEntryOption.PRESERVE_TEXT,
    },
  ];
  if (BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH_PUBLIC_DATA) {
    tagConfigs.push({
      markdownTag: 'web_citation',
      htmlTagName: 'webcitationchip',
      properties: [BenchlingTagProperty.LINK_URL],
      validator: (content: string) => {
        try {
          const url = new URL(content);
          return url.protocol === 'http:' || url.protocol === 'https:';
        } catch (error) {
          // Expected: URL constructor throws for invalid URLs, return false for validation
          if (error instanceof TypeError) {
            return false;
          }
          throw error;
        }
      },
      component: WebCitationComponent,
      preserveContentWhenSaving: SaveToEntryOption.PRESERVE_TAGS,
      savingComponent: WebCitationSavingComponent,
    });
  }
  return {tagConfigs};
};

const BASE_SAFE_MARKDOWN_TAGS = [
  // NOTE: We purposefully don't include img, script, etc. to limit risk.
  // NOTE: anchor tags (a) are allowed but hrefs are restricted to allowlisted domains only
  // headers
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  // text
  'p',
  'span',
  // rule & break
  'hr',
  'br',
  // code
  'code',
  'pre',
  'bnch_code',
  // lists
  'li',
  'ul',
  'ol',
  // italics
  'em',
  // bold
  'strong',
  'b',
  // quotes
  'blockquote',
  'q',
  // strikethrough
  's',
  'del',
  // tables
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  // links (with allowlist restriction)
  'a',
];

// Creates safe markdown tags list including custom elements from config
const createSafeMarkdownTags = (tagConfigs: BenchlingTagConfigs): Array<string> => [
  ...BASE_SAFE_MARKDOWN_TAGS,
  ...tagConfigs.tagConfigs.map((config) => config.htmlTagName),
];

// Creates a sanitize schema for the markdown tags
export const createSanitizeSchema = (
  tagConfigs: BenchlingTagConfigs
): {
  tagNames: Array<string>;
  attributes: Record<string, Array<string>>;
} => ({
  tagNames: createSafeMarkdownTags(tagConfigs),
  attributes: {
    '*': [],
    // Allow href, target, and rel on anchor tags (validation happens in custom component)
    a: ['href', 'target', 'rel'],
    // Allow language and code on bnch_code elements (set by remarkCodeBlock plugin)
    bnch_code: ['language', 'code'],
    ...Object.fromEntries(tagConfigs.tagConfigs.map((config) => [config.htmlTagName, config.properties])),
  },
});

export interface LLMMarkdownProps {
  citationNumberMap?: Map<string, number>;
  markdown: string;
  onCitationClick?: (toolId: string, isSourceCitation?: boolean) => void;
  progressData?: ProgressData;
  chatSessionId?: string;
  classNames?: LLMMarkdownClassNames;
  /**
   * Whether to show the per-chart "Add to entry" button on rendered charts. Defaults to false;
   * callers in interactive (non-readonly) chat contexts should opt in.
   */
  canSaveChartsToEntry?: boolean;
}

/**
 * Component for fenced code blocks, rendered from the custom `bnch_code` element
 * produced by the remarkCodeBlock plugin.
 *
 * Short code blocks (fewer than CODE_BLOCK_INLINE_LINE_LIMIT lines) are always
 * rendered inline. Longer blocks are collapsed behind a "View code" button and
 * can be expanded/collapsed in place.
 */
const CODE_BLOCK_INLINE_LINE_LIMIT = 5;

function CodeBlockComponent(props: Record<string, unknown>): JSX.Element {
  const code = (props.code as string) ?? '';
  const [isExpanded, setIsExpanded] = useState(false);

  const lineCount = code.split('\n').length;

  if (lineCount < CODE_BLOCK_INLINE_LINE_LIMIT) {
    return (
      <pre>
        <code>{code}</code>
      </pre>
    );
  }

  return (
    <div className={css(styles.codeBlock)}>
      <div
        className={css(styles.viewCode)}
        onClick={() => {
          setIsExpanded((prev) => !prev);
        }}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsExpanded((prev) => !prev);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
      >
        <BenchlingIcon icon="code" /> {isExpanded ? 'Hide code' : 'View code'}
      </div>
      {isExpanded && (
        <pre>
          <code>{code}</code>
        </pre>
      )}
    </div>
  );
}

/**
 * Custom anchor tag component that validates URLs against allowlist.
 * Renders allowed URLs as links with security attributes, otherwise renders as plain text.
 */
function AllowlistAnchor({href, children}: React.AnchorHTMLAttributes<HTMLAnchorElement>): JSX.Element {
  // If no href or href is not in allowlist, render as plain text
  if (!href || !isAllowedUrl(href)) {
    return <span>{children}</span>;
  }
  // For allowed URLs, render as link with security attributes
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

export default function LLMMarkdownSection({
  citationNumberMap,
  markdown,
  onCitationClick,
  progressData,
  chatSessionId,
  classNames: markdownClassNames,
  canSaveChartsToEntry = false,
}: LLMMarkdownProps): JSX.Element {
  const markdownDependencies = React.useMemo(
    () => ({
      onCitationClick,
      progressData,
      citationNumberMap,
      chatSessionId,
      classNames: markdownClassNames,
      canSaveChartsToEntry,
    }),
    [
      onCitationClick,
      progressData,
      citationNumberMap,
      chatSessionId,
      markdownClassNames,
      canSaveChartsToEntry,
    ]
  );
  const tagConfigs = createBenchlingTagConfigs();
  const components: Record<string, React.ComponentType<Record<string, unknown>>> = {
    ...Object.fromEntries(tagConfigs.tagConfigs.map((config) => [config.htmlTagName, config.component])),
    a: AllowlistAnchor as React.ComponentType<Record<string, unknown>>,
    bnch_code: CodeBlockComponent,
    p: MarkdownParagraphWithTableMetadata,
    h2: MarkdownH2WithTableMetadata,
    h3: MarkdownH3WithTableMetadata,
    h4: MarkdownH4WithTableMetadata,
  };

  const sanitizeSchema = createSanitizeSchema(tagConfigs);

  return (
    <LLMMarkdownDependenciesContext.Provider value={markdownDependencies}>
      <section {...getTestProps({component: 'LLMMarkdownSection', element: 'root'})}>
        <LLMMarkdownTableMetadataProvider>
          <ReactMarkdown
            allowedElements={sanitizeSchema.tagNames}
            remarkPlugins={[[remarkBenchlingTags, tagConfigs], remarkGfmTables, remarkCodeBlock]}
            rehypePlugins={[
              [rehypeBenchlingTags, tagConfigs],
              [rehypeSanitize, sanitizeSchema],
            ]}
            components={components}
          >
            {markdown}
          </ReactMarkdown>
        </LLMMarkdownTableMetadataProvider>
      </section>
    </LLMMarkdownDependenciesContext.Provider>
  );
}

const styles = StyleSheet.create({
  webCitationContainer: {
    display: 'inline-block',
    marginLeft: '2px',
    marginRight: '2px',
  },
  webCitationLink: {
    textDecoration: 'none',
    color: 'inherit',
  },
  paragraph: {
    margin: '1em 0',
    lineHeight: '1.5',
  },
  codeBlock: {
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: 4,
    margin: '8px 0',
  },
  viewCode: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '6px 12px',
    color: GRAY_DARK,
    fontSize: 12,
    cursor: 'pointer',
  },
});
